import { useQuery } from 'react-query'
import { AcceptInvite, ErrorResponse, Invite, InvitesApi, User } from '@citruscamps/citrus-client'
import { ROUTES } from '../../../../../constants/routes'
import { useRouter } from '../../../../../hooks/useRouter'
import { useToast } from '../../../../../hooks/useToast'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateItemKey } from '../../../../../utils/key-generator'
import { toQueryParams } from '../../../../../utils/query-params'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'

interface IProps {
  user?: User
  code?: string
}

interface FetchInvite {
  data?: Invite
  isLoading: boolean
  isError: boolean
  error: ErrorResponse | null
  handleAccept: (value: AcceptInvite) => Promise<void>
}

export const useFetchInvite = ({ user, code }: IProps): FetchInvite => {
  const { requestHandler } = useRequestHandler()
  const router = useRouter()
  const { setToast } = useToast()
  const queryKeys = generateItemKey({
    type: 'invite',
    program_id: undefined,
    id: code,
  })
  const { data, error, isError, isLoading } = useQuery<Invite, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!code) {
        throw new Error('Unable to fetch data')
      }
      const client = new InvitesApi(generateApiConfig())
      return await requestHandler<Invite>(() => client.findOneInvite({ code }, { signal }))
    },
    { enabled: !!code },
  )

  const handleAccept = async (value: AcceptInvite): Promise<void> => {
    if (code) {
      const client = new InvitesApi(generateApiConfig())
      const invite = await requestHandler<Invite>(
        () => client.acceptInvite({ code, AcceptInvite: value }),
        {
          toast: {
            setToast,
          },
        },
      )
      if (user) {
        const params = toQueryParams({ program_id: invite.program.id })
        const search = params ? `?${params}` : undefined
        await router.push({ pathname: ROUTES.DASHBOARD, search })
      } else {
        await router.push(ROUTES.LOGIN)
      }
    }
  }

  return {
    data,
    error,
    isError,
    isLoading,
    handleAccept,
  }
}
